import {FC, useMemo, useEffect} from "react";
import {Box, LinearProgress, Switch, Tooltip, Stack} from "@mui/material";
import {DataGridPro, GridInitialState, GridColDef, GRID_CHECKBOX_SELECTION_COL_DEF, getGridSingleSelectOperators} from "@mui/x-data-grid-pro";
import {ENROLLMENT_TYPE_FILTER_VALUES} from "@/app2/shared/constants/enrollmentTypes";
import {makeStyles} from "tss-react/mui";
import {CourseEnrollmentsGridToolbar} from "./Toolbar/CourseEnrollmentsGridToolbar";
import {useCourseEnrollmentsGrid} from "./hooks/useCourseEnrollmentsGrid";
import {CourseEnrollmentDto} from "@/app2/shared/data";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import type {GetCohortSettingsResponse} from "@/app2/shared/data/gen";
import useCustomDataGrid from "@/app2/shared/hooks/useCustomDataGrid";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {formatDateForDisplay} from "@/app2/shared/utils/dateManipulation";

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		padding: 0,
		border: 0
	},
	progressBarRoot: {
		width: "100%",
		alignItems: "center",
		gap: theme.spacing(2)
	},
	progressBar: {
		width: "100%"
	}
}));

interface CourseEnrollmentsGridProps {
	courseId: number;
	cohortId: number;
	/** Cohort settings for custom field column visibility and labels (same as Team Progress). */
	organization?: GetCohortSettingsResponse | null;
	isCoursePublished: boolean;
	selected: number[];
	setSelected: (ids: number[]) => void;
	setSelectedSingular: (id: number | undefined) => void;
	handleEnroll: (userIds: number[]) => void;
	handleUnenroll: (userIds: number[]) => void;
	handleMarkComplete: (userIds: number[]) => void;
	handleManageDueDates: (userIds: number[]) => void;
}

export const CourseEnrollmentsGrid: FC<CourseEnrollmentsGridProps> = ({
	courseId,
	cohortId,
	organization,
	isCoursePublished,
	selected,
	setSelected,
	setSelectedSingular,
	handleEnroll,
	handleUnenroll,
	handleMarkComplete,
	handleManageDueDates
}) => {
	const {classes} = useStyles();
	const {t} = useSSTranslation();
	const enrollmentTypeFilterOperators = getGridSingleSelectOperators().filter(
		(op) => op.value === "is"
	);

	const {
		api: {
			assignFilters
		}} = useCustomDataGrid<CourseEnrollmentDto>({});	

	const {
		enrollments,
		rowCount,
		isFetching,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		sortModel,
		onSortModelChange,
		onRowSelectionChange,
		defaultInitialState,
		refetch,
		refetchCourseData
	} = useCourseEnrollmentsGrid({
		courseId,
		cohortId,
		selected,
		setSelected
	});

	const {closeSnackbar, successSnackbar, errorSnackbar} = useCustomSnackbar();

	// Enroll: lesson/module enrollments may finish after HTTP returns — listen for WS completion to refetch and toast.
	// Unenroll: course enrollment row is correct when HTTP succeeds; useBulkUnenrollUsers already invalidates this grid — no WS listener.
	useEffect(() => {
		const handleCourseEnrollmentsCompleted = (event: CustomEvent) => {
			const {inProgress, isSuccessful, errorMessage, silentRefresh} = event.detail;
			if (!inProgress) {
				// Clear selections and refetch data to show updated enrollment status
				setSelected([]);
				setSelectedSingular(undefined);
				closeSnackbar(null);

				if (!silentRefresh) {
					if (isSuccessful) {
						successSnackbar(
							t("notifications_enrollment_success", "Users enrolled successfully."),
							true
						);
					} else {
						const message =
							errorMessage ||
							t("notifications_enrollment_error", "Unable to enroll users. Please try again.");
						errorSnackbar(message, true, 10000);
					}
				} else if (!isSuccessful) {
					const message =
						errorMessage ||
						t("notifications_enrollment_error", "Unable to enroll users. Please try again.");
					errorSnackbar(message, true, 10000);
				}

				refetch();
				refetchCourseData();
			}
		};

		window.addEventListener("courseEnrollmentsCompleted", handleCourseEnrollmentsCompleted as EventListener);

		return () => {
			window.removeEventListener("courseEnrollmentsCompleted", handleCourseEnrollmentsCompleted as EventListener);
		};
	}, [refetch, setSelected, setSelectedSingular, closeSnackbar, successSnackbar, errorSnackbar, t]);

	// Memoize rows to ensure proper re-rendering
	const rows = useMemo(() => enrollments, [enrollments]);

	const columns: GridColDef<CourseEnrollmentDto>[] = useMemo(() => {
		const baseColumns: GridColDef<CourseEnrollmentDto>[] = [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				disableColumnMenu: true
			},
			{
				field: "firstname",
				flex: 1,
				headerName: t("common_first_name", "First Name"),
				type: "string"
			},
			{
				field: "lastname",
				flex: 1,
				headerName: t("common_last_name", "Last Name"),
				type: "string"
			},
			{
				field: "employeeCode",
				flex: 1,
				headerName: t("common_employee_code", "Employee Code"),
				type: "string"
			},
			{
				field: "email",
				flex: 1,
				headerName: t("common_email", "Email"),
				type: "string"
			},
			{
				field: "jobCategory",
				flex: 1,
				headerName: t("common_job_category", "Job Category"),
				type: "string"
			},
			{
				field: "jobTitle",
				flex: 1,
				headerName: t("common_job_title", "Job Title"),
				type: "string"
			},
			{
				field: "hireDate",
				flex: 1,
				headerName: t("common_hire_date", "Hire Date"),
				type: "date",
				valueGetter: (params: any) =>
					params.row.hireDate ? new Date(params.row.hireDate) : null,
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date, {timeZone: "UTC"});
				}
			},
			{
				field: "titleStartDate",
				flex: 1,
				headerName: t("common_title_start_date", "Title Start Date"),
				type: "date",
				valueGetter: (params: any) =>
					params.row.titleStartDate ? new Date(params.row.titleStartDate) : null,
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date, {timeZone: "UTC"});
				}
			},
			{
				field: "enrolled",
				flex: 1,
				headerName: t("common_enroll", "Enroll"),
				type: "boolean",
				renderCell: (params: any) => {
					return (
						<Tooltip
							title={
								!isCoursePublished
									? t(
										"course_draft_not_enrollment_description",
										"Please publish the course to enable user enrollments.",
									  )
									: ""
							}
						>
							<span>
								<Switch
									checked={params.value as boolean}
									disabled={!isCoursePublished}
									onClick={(x) => {
										x.stopPropagation();
										setSelectedSingular(params.row.id);
										if (params.value) {
											handleUnenroll([params.row.id]);
										} else {
											handleEnroll([params.row.id]);
										}
									}}
								/>
							</span>
						</Tooltip>
					);
				}
			},
			{
				field: "dueDate",
				flex: 1,
				headerName: t("common_due_date", "Due Date"),
				type: "date",
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date | string, {timeZone: "UTC"});
				}
			},
			{
				field: "assignedOn",
				flex: 1,
				headerName: t("common_assigned_on", "Assigned On"),
				type: "date",
				valueFormatter: (params: any) => {
					if (!params.value) return "";
					return formatDateForDisplay(params.value as Date | string, {timeZone: "UTC"});
				}
			},
			{
				field: "enrollmentType",
				flex: 1,
				headerName: t("common_enrollment_type", "Enrollment Type"),
				type: "singleSelect",
				filterOperators: enrollmentTypeFilterOperators,
				valueOptions: ENROLLMENT_TYPE_FILTER_VALUES.map((value) => ({
					value,
					label: t(`user_enrollment_type_${value}`, value)
				})),
				valueGetter: (params: any) =>
					params.value ? t(`user_enrollment_type_${params.value}`, params.value as string) : ""
			},
			{
				field: "progress",
				flex: 1,
				headerName: t("common_course_completion", "Course Completion"),
				type: "number",
				renderCell: (params: any) => {
					return (
						<Stack className={classes.progressBarRoot} direction="row">
							<LinearProgress
								className={classes.progressBar}
								variant="determinate"
								value={params.value ?? 0}
							/>
							<span>{`${params.value ?? 0}%`}</span>
						</Stack>
					);
				}
			},
			{
				field: "division",
				flex: 1,
				type: "string",
				headerName: t("common_division", "Division")
			},
			{
				field: "supervisorName",
				flex: 1,
				type: "string",
				headerName: t("common_supervisor", "Supervisor")
			}
		];

		// Add custom field columns when enabled in cohort settings (same pattern as Team Progress)
		if (organization?.isActiveCustomField1) {
			baseColumns.push({
				field: "customFieldValue1",
				headerName: String(organization.customFieldName1 ?? "Custom Field 1"),
				flex: 1,
				type: "string"
			});
		}
		if (organization?.isActiveCustomField2) {
			baseColumns.push({
				field: "customFieldValue2",
				headerName: String(organization.customFieldName2 ?? "Custom Field 2"),
				flex: 1,
				type: "string"
			});
		}
		if (organization?.isActiveCustomField3) {
			baseColumns.push({
				field: "customFieldValue3",
				headerName: String(organization.customFieldName3 ?? "Custom Field 3"),
				flex: 1,
				type: "string"
			});
		}

		return baseColumns;
	}, [isCoursePublished, setSelectedSingular, handleEnroll, handleUnenroll, t, classes, organization]);

	const Toolbar = (): JSX.Element => (
		<CourseEnrollmentsGridToolbar
			selectedRows={selected}
			isCoursePublished={isCoursePublished}
			handleEnroll={handleEnroll}
			handleUnenroll={handleUnenroll}
			handleMarkComplete={handleMarkComplete}
			handleManageDueDates={handleManageDueDates}
		/>
	);

	return (
		<Box>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DataGridPro<CourseEnrollmentDto>
					autoHeight
					className={classes.dataGrid}
					loading={isFetching}
					sortingMode="server"
					page={page}
					keepNonExistentRowsSelected
					pageSize={pageSize}
					onPageSizeChange={onPageSizeChange}
					onPageChange={onPageChange}
					checkboxSelection
					onSelectionModelChange={onRowSelectionChange}
					paginationMode="server"
					pagination
					rowCount={rowCount}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					rows={rows as any}
					columns={columns.map(e => assignFilters(e as any))}
					filterMode="server"
					onFilterModelChange={onFilterChange}
					components={{Toolbar}}
					rowsPerPageOptions={[25, 50, 100, 200]}
					initialState={defaultInitialState}
				/>
			</LocalizationProvider>

		</Box>
	);
};
